import type { FC } from 'react';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import type { SearchResultsViewProps } from '../types';
import {
    ResultsContainer,
    ResultCard,
    ResultHeader,
    CustomerInfo,
    CustomerName,
    ContactInfo,
    VehicleInfo,
    ResultContentGrid,
    EmptyStateContainer
} from '../CreditAppSearch.styles';
import { formatPhoneNumber } from '../../utils/formatUtils';
import { ServiceUnavailableAlert } from '../../common/ServiceUnavailableAlert';

/**
 * Search results view component for credit app search
 * Displays error alerts or result cards (loading is handled by parent)
 */
export const SearchResultsView: FC<SearchResultsViewProps> = ({ results, error, onAttach, isLocked = false }) => {
    // Error state - display alert banner or empty state for no results
    if (error) {
        if (error.hasNoResults) {
            return (
                <EmptyStateContainer data-testid="search-no-results">
                    <Typography variant="strong-sm" color="sem.color.surface.button.primary.rest">
                        No Results
                    </Typography>
                </EmptyStateContainer>
            );
        }
        if (error.hasError) {
            return <ServiceUnavailableAlert />;
        }
    }

    // No results - show empty state message
    if (results.length === 0) {
        return (
            <EmptyStateContainer data-testid="search-empty-state">
                <Typography variant="strong-sm" color="sem.color.surface.button.primary.rest">
                    Enter search details to begin
                </Typography>
            </EmptyStateContainer>
        );
    }

    // Results display
    return (
        <ResultsContainer data-testid="search-results-container">
            {results.map((result) => (
                <ResultCard key={result.creditAppId} data-testid="search-result-card">
                    <ResultHeader>
                        <Typography variant="body-xs" data-testid="created-date">
                            Created Date: {result.createdDate}
                        </Typography>
                    </ResultHeader>

                    <CustomerInfo>
                        <CustomerName data-testid="customer-name">{result.customerName}</CustomerName>
                        <Button
                            buttonStyle="primary"
                            size="medium"
                            onClick={() => onAttach(result.creditAppId)}
                            disabled={isLocked}
                            data-testid="attach-button"
                        >
                            Attach
                        </Button>
                    </CustomerInfo>

                    <ResultContentGrid>
                        <ContactInfo>
                            <Typography variant="body-xs" data-testid="customer-phone">
                                Phone: {formatPhoneNumber(result.phone)}
                            </Typography>
                            <Typography variant="body-xs" data-testid="customer-email">
                                Email: {result.email}
                            </Typography>
                            <Typography variant="body-xs" data-testid="credit-id">
                                Credit ID: {result.creditAppId}
                            </Typography>
                        </ContactInfo>
                        <VehicleInfo>
                            <Typography variant="body-xs" data-testid="vehicle-description">
                                Vehicle: {result.vehicleYear} {result.vehicleMake} {result.vehicleModel}{' '}
                                {result.vehicleTrim && result.vehicleTrim}
                            </Typography>
                            <Typography variant="body-xs" data-testid="vehicle-vin">
                                VIN: {result.vin}
                            </Typography>
                            <Typography variant="body-xs" data-testid="vehicle-stock">
                                Stock#: {result.stockNumber}
                            </Typography>
                        </VehicleInfo>
                    </ResultContentGrid>
                </ResultCard>
            ))}
        </ResultsContainer>
    );
};
